import { Anchor, Card, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import {
  IconArrowsExchange,
  IconBox,
  IconClipboardList,
  IconCoin,
  IconFileInvoice,
  IconGauge,
  IconListSearch,
  IconPackages,
  IconReceipt2,
  IconReportMoney,
  IconScale,
  IconShoppingCart,
  IconTruckDelivery,
  type Icon,
} from '@tabler/icons-react';
import { useTenantFeatures } from '../../../lib/tenantFeatures';

interface ReportLink {
  to: string;
  title: string;
  description: string;
  icon: Icon;
}

/** All 12 BUILD_SPEC.md §13 reports. Mass-balance, stock-on-hand, movement
 * ledger, and reorder are the richest screens (goal text); the rest share the
 * filter+table+CSV template. */
const REPORTS: ReportLink[] = [
  {
    to: '/reports/stock-on-hand',
    title: 'Stock on hand & valuation',
    description: 'Product x location, at pool weighted-average cost.',
    icon: IconPackages,
  },
  {
    to: '/reports/movements',
    title: 'Movement ledger browser',
    description: 'Every posted stock movement, filterable and paginated.',
    icon: IconListSearch,
  },
  {
    to: '/reports/mass-balance',
    title: 'Mass balance',
    description: 'Site x period dry-mass identity, with the residual highlighted.',
    icon: IconScale,
  },
  {
    to: '/reports/yield',
    title: 'Yield by stage/run',
    description: 'Actual vs recipe dry-mass yield, weighted by input mass.',
    icon: IconGauge,
  },
  {
    to: '/reports/silo-drift',
    title: 'Silo drift history',
    description: 'Estimated vs ledger mass over time, per silo.',
    icon: IconArrowsExchange,
  },
  {
    to: '/reports/purchasing',
    title: 'PO book & supplier spend',
    description: 'Open/partial purchase orders and posted supplier spend.',
    icon: IconTruckDelivery,
  },
  {
    to: '/reports/sales',
    title: 'Sales by customer/product',
    description: 'Posted dispatch value, cost and margin, grouped.',
    icon: IconReceipt2,
  },
  {
    to: '/reports/uninvoiced-dispatches',
    title: 'Uninvoiced dispatches',
    description: 'Posted dispatch lines with no invoice line yet.',
    icon: IconFileInvoice,
  },
  {
    to: '/reports/consumable-spend',
    title: 'Consumable spend by cost centre',
    description: 'Stores-issue spend, grouped by cost centre.',
    icon: IconCoin,
  },
  {
    to: '/reports/reorder',
    title: 'Reorder suggestions',
    description: 'Products below their minimum level, by preferred supplier.',
    icon: IconShoppingCart,
  },
  {
    to: '/reports/margin',
    title: 'Margin per dispatch/invoice',
    description: 'Sale price vs weighted-average cost.',
    icon: IconReportMoney,
  },
  {
    to: '/reports/genealogy',
    title: 'Genealogy (by dispatch)',
    description: 'Time-windowed provenance of an outbound load.',
    icon: IconClipboardList,
  },
];

const REGISTERS: ReportLink[] = [
  {
    to: '/reports/stock-on-hand',
    title: 'Stock on hand by location',
    description: 'Same screen as the report above — browse by location.',
    icon: IconPackages,
  },
  {
    to: '/reports/movements',
    title: 'Movements (the ledger)',
    description: 'Same screen as the report above — browse every posting.',
    icon: IconListSearch,
  },
  {
    to: '/reports/products',
    title: 'Products',
    description: 'Read-only register of every product master.',
    icon: IconBox,
  },
];

function ReportCard({ report }: { report: ReportLink }) {
  return (
    <Card component={Link} to={report.to} withBorder radius="md" p="md" style={{ cursor: 'pointer' }}>
      <Stack gap={6}>
        <report.icon size={22} stroke={1.5} />
        <Text fw={600}>{report.title}</Text>
        <Text size="sm" c="dimmed">
          {report.description}
        </Text>
      </Stack>
    </Card>
  );
}

/** Reports hub (plan.md G25 — BUILD_SPEC.md §13): landing page listing all 12
 * reports plus the "browse the data" registers. */
export function ReportsHub() {
  // "Silo drift history" hidden when the tenant's silo module is off (Stuart
  // ruling 2026-08-26, default OFF — Howick has none). The report screen and
  // its API endpoint stay live either way (this goal's scope is UI-hiding
  // only) — just not linked from here.
  const { silosEnabled } = useTenantFeatures();
  const reports = silosEnabled ? REPORTS : REPORTS.filter((r) => r.to !== '/reports/silo-drift');

  return (
    <Stack gap="xl">
      <Stack gap={2}>
        <Title order={2}>Reports</Title>
        <Text c="dimmed">Every §13 report — filterable, CSV-exportable.</Text>
      </Stack>

      <Stack gap="sm">
        <Title order={4}>Registers</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {REGISTERS.map((r) => (
            <ReportCard key={r.title} report={r} />
          ))}
        </SimpleGrid>
      </Stack>

      <Stack gap="sm">
        <Title order={4}>Reports</Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {reports.map((r) => (
            <ReportCard key={r.to} report={r} />
          ))}
        </SimpleGrid>
      </Stack>

      <Text size="xs" c="dimmed">
        Looking for something else?{' '}
        <Anchor component={Link} to="/search" size="xs">
          Try global search
        </Anchor>
        .
      </Text>
    </Stack>
  );
}
